import { useEffect, useState } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Coins, Check, Sparkles, CloudRain, Warehouse } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (p) => (!p ? null : p.startsWith("http") ? p : p.startsWith("/api/") ? `${BACKEND}${p}` : p);

const Placeholder = ({ name }) => (
  <div className="flex h-28 w-full flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border bg-muted/40 text-muted-foreground">
    <Warehouse size={22} className="opacity-60" />
    <span className="text-[11px] font-bold uppercase tracking-wide">{name}</span>
    <span className="text-[9px]">Artwork coming soon</span>
  </div>
);

export const StableUpgrades = ({ onBack, onUpdate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => { setLoading(true); gameApi.get("/game/upgrades").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const buy = async (u) => {
    setBusy(true);
    try {
      const { data: res } = await gameApi.post("/game/upgrades/buy", { upgrade_id: u.id });
      if (res.player && onUpdate) onUpdate(res.player);
      toast.success(res.message || `Purchased ${u.name}`);
      setConfirm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not purchase"); } finally { setBusy(false); }
  };

  return (
    <div className="pb-10" data-testid="stable-upgrades-view">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
        <button data-testid="upgrades-back" onClick={onBack} className="rounded-full p-1 text-muted-foreground"><ArrowLeft size={20} /></button>
        <Sparkles className="text-secondary" size={20} /><h2 className="font-display text-xl font-bold text-primary">Stable Upgrades</h2>
        {data && <span className="ml-auto flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700" data-testid="upgrades-coins"><Coins size={13} /> {data.currency.toLocaleString()}</span>}
      </div>
      <div className="px-4 pt-3">
        <p className="mb-3 text-xs text-muted-foreground">Permanent facilities that boost your training rewards for every horse, on every future makeover.</p>
        {loading ? <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading…</p>
          : !data ? <p className="mt-6 text-center text-sm text-muted-foreground">Couldn't load upgrades.</p>
          : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {data.upgrades.filter((u) => u.active).map((u) => {
                const noPrice = u.price === null || u.price === undefined;
                const isStall = u.kind === "stall";
                const isConsumable = u.kind === "consumable";
                const owned = u.owned;
                const canBuy = u.purchasable && !noPrice && (isStall ? !owned : !owned);
                return (
                  <div key={u.id} data-testid={`upgrade-card-${u.id}`} className="flex flex-col rounded-2xl border border-border bg-card p-3">
                    {u.image_path ? <img data-testid={`upgrade-img-${u.id}`} src={imgUrl(u.image_path)} alt={u.name} className="h-28 w-full rounded-xl object-cover" />
                      : <Placeholder name={u.name} />}
                    <div className="mt-2 flex items-center gap-1.5">
                      <h3 className="font-display text-base font-bold text-primary">{u.name}</h3>
                      {u.weather_protection && <CloudRain size={13} className="text-sky-500" title="Weather protection" />}
                      {owned && !isStall && !isConsumable && <span data-testid={`upgrade-owned-${u.id}`} className="ml-auto rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-700">Owned</span>}
                      {isConsumable && <span data-testid={`upgrade-count-${u.id}`} className="ml-auto rounded-full bg-secondary/15 px-2 py-0.5 text-[10px] font-bold text-secondary">{u.owned_count || 0} unused</span>}
                      {isStall && <span className="ml-auto rounded-full bg-secondary/15 px-2 py-0.5 text-[10px] font-bold text-secondary">{data.owned_stalls}/{data.total_stalls} stalls</span>}
                    </div>
                    <p className="mt-1 text-[11px] font-semibold text-secondary">{u.benefit}</p>
                    <p className="mt-1 flex-1 text-[11px] text-muted-foreground">{u.description}</p>
                    {owned && u.purchase_date && !isConsumable && <p className="mt-1 text-[10px] text-muted-foreground">Purchased {new Date(u.purchase_date).toLocaleDateString()}</p>}
                    <div className="mt-2">
                      {owned && !isStall && !isConsumable ? <div className="flex items-center justify-center gap-1 rounded-lg bg-green-100 px-3 py-2 text-center text-xs font-bold text-green-700" data-testid={`upgrade-ownedbtn-${u.id}`}><Check size={13} /> Owned</div>
                        : owned && isStall && (data.owned_stalls >= data.total_stalls) ? <div className="rounded-lg bg-green-100 px-3 py-2 text-center text-xs font-bold text-green-700">All stalls unlocked</div>
                        : noPrice ? <div className="rounded-lg bg-muted px-3 py-2 text-center text-xs font-bold text-muted-foreground" data-testid={`upgrade-noprice-${u.id}`}>Price coming soon</div>
                        : <button data-testid={`upgrade-buy-${u.id}`} disabled={!canBuy} onClick={() => setConfirm(u)}
                            className="flex h-10 w-full items-center justify-center gap-1.5 rounded-lg bg-primary text-sm font-bold text-primary-foreground disabled:opacity-50 active:scale-95">
                            <Coins size={14} /> {isStall ? "Unlock Stall" : isConsumable ? "Buy Groom" : "Purchase"} · {u.price.toLocaleString()}
                          </button>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-black/50 p-4" data-testid="upgrade-confirm">
          <div className="w-full max-w-sm rounded-2xl bg-card p-5 text-center shadow-2xl">
            <Sparkles className="mx-auto text-secondary" size={26} />
            <p className="mt-2 font-display text-lg font-bold text-primary">Purchase {confirm.name}?</p>
            <p className="mt-1 text-sm text-muted-foreground">{confirm.kind === "consumable"
              ? <>This will deduct <b>{confirm.price.toLocaleString()} coins</b> and add a Groom Service to your inventory. Assign it to a horse from that horse's Tack tab.</>
              : <>This will deduct <b>{confirm.price.toLocaleString()} coins</b> and permanently add it to your stable.</>}</p>
            <div className="mt-4 flex gap-2">
              <button data-testid="upgrade-confirm-cancel" onClick={() => setConfirm(null)} className="h-11 flex-1 rounded-xl border border-border font-bold text-primary active:scale-95">Cancel</button>
              <button data-testid="upgrade-confirm-buy" disabled={busy} onClick={() => buy(confirm)} className="h-11 flex-1 rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-60 active:scale-95">{busy ? "…" : "Purchase"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- Jo-only admin: Stable Upgrade Art & pricing ----------------
export const StableUpgradeArt = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => gameApi.get("/game/admin/upgrades").then((r) => setRows(r.data.upgrades)).catch(() => setRows([]));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const patch = (id, i, field, val) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const save = async (u) => {
    setBusy(true);
    try {
      const body = { description: u.description, active: u.active, purchasable: u.purchasable };
      if (u.price === "" || u.price === null) body.clear_price = true; else body.price = Number(u.price);
      await gameApi.post(`/game/admin/upgrades/${u.id}`, body);
      toast.success(`${u.name} saved`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const upload = async (u, file) => {
    if (!file) return; setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await gameApi.post(`/game/admin/upgrades/${u.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Artwork uploaded"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  const removeImg = async (u) => { setBusy(true); try { await gameApi.post(`/game/admin/upgrades/${u.id}/image/remove`); toast.success("Artwork removed"); load(); } catch { toast.error("Failed"); } finally { setBusy(false); } };

  if (!rows) return <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading…</p>;
  return (
    <div className="mt-3 space-y-3" data-testid="stable-upgrade-art">
      <p className="text-[11px] text-muted-foreground">Upload artwork, set prices and edit each Stable Upgrade. Upgrades with no price show “Price coming soon” to players and cannot be bought.</p>
      {rows.map((u, i) => (
        <div key={u.id} data-testid={`upgrade-admin-${u.id}`} className="rounded-xl border border-border bg-card p-3">
          <div className="flex gap-3">
            <div className="w-24 shrink-0">
              {u.image_path ? <img src={imgUrl(u.image_path)} alt={u.name} className="h-20 w-24 rounded-lg object-cover" /> : <Placeholder name={u.name} />}
              <label className="mt-1 block cursor-pointer rounded bg-primary px-2 py-1 text-center text-[10px] font-bold text-primary-foreground" data-testid={`upgrade-admin-upload-${u.id}`}>Upload<input type="file" accept="image/png,image/webp,image/jpeg" className="hidden" onChange={(e) => upload(u, e.target.files?.[0])} /></label>
              {u.image_path && <button onClick={() => removeImg(u)} className="mt-1 w-full rounded border border-border px-2 py-1 text-[10px] font-bold text-destructive">Remove</button>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2"><b className="text-sm text-primary">{u.name}</b><span className="rounded-full bg-secondary/15 px-2 py-0.5 text-[10px] font-bold text-secondary">{u.owned_count} owned</span></div>
              <div className="mt-1 flex items-center gap-2">
                <label className="text-[11px] font-semibold text-muted-foreground">Price</label>
                <input data-testid={`upgrade-admin-price-${u.id}`} type="number" value={u.price ?? ""} placeholder="none" onChange={(e) => patch(u.id, i, "price", e.target.value)} className="w-28 rounded border border-border bg-background px-2 py-1 text-xs" />
                <span className="text-[10px] text-muted-foreground">blank = Price coming soon</span>
              </div>
              <textarea data-testid={`upgrade-admin-desc-${u.id}`} value={u.description} onChange={(e) => patch(u.id, i, "description", e.target.value)} rows={2} className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-xs" />
              <div className="mt-1 flex items-center gap-3">
                <label className="flex items-center gap-1 text-[11px] font-semibold text-primary"><input type="checkbox" checked={u.active} onChange={(e) => patch(u.id, i, "active", e.target.checked)} /> Active</label>
                <label className="flex items-center gap-1 text-[11px] font-semibold text-primary"><input type="checkbox" checked={u.purchasable} onChange={(e) => patch(u.id, i, "purchasable", e.target.checked)} /> Purchasable</label>
                <button data-testid={`upgrade-admin-save-${u.id}`} disabled={busy} onClick={() => save(u)} className="ml-auto rounded bg-secondary px-3 py-1 text-xs font-bold text-white disabled:opacity-60">Save</button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
